// src/pages/CheckoutPageNew.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { generateIdempotencyKey } from '../utils/idempotency';
import AuthStep from '../components/AuthStep';
import GraiaPaymentForm, { CardFormData } from '../components/GraiaPaymentForm';
import {
    ShoppingCart, CreditCard, User, Shield, CheckCircle,
    Trash2, Tag, X, AlertCircle,
} from 'lucide-react';
import './CheckoutPageNew.css';

type CheckoutStep = 'cart' | 'auth' | 'payment';

const CheckoutPageNew: React.FC = () => {
    const navigate = useNavigate();
    const { items, removeItem, clearCart, getTotal } = useCart();
    const { user, isAuthenticated } = useAuth();

    const [currentStep, setCurrentStep] = useState<CheckoutStep>('cart');
    const [couponCode, setCouponCode] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
    const [couponLoading, setCouponLoading] = useState(false);
    const [discount, setDiscount] = useState(0);
    const [processingPayment, setProcessingPayment] = useState(false);
    const [paymentError, setPaymentError] = useState('');
    const [ownedProducts, setOwnedProducts] = useState<Set<string>>(new Set());
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [purchasedType, setPurchasedType] = useState<string>('');

    // Pix state
    const [pixData, setPixData] = useState<{ qrCode: string; qrCodeBase64: string; ticketUrl?: string } | null>(null);
    const [pixPolling, setPixPolling] = useState(false);

    // Boleto state
    const [boletoUrl, setBoletoUrl] = useState<string | null>(null);

    useEffect(() => {
        const initMP = () => {
            const MP = (window as any).MercadoPago;
            if (!MP || (window as any).__mpInstance) return;
            (window as any).__mpInstance = new MP(
                process.env.REACT_APP_MERCADOPAGO_PUBLIC_KEY || 'TEST-679ef9e2-e406-4206-a4ba-092837b0828a',
                { locale: 'pt-BR' }
            );
        };
        if ((window as any).MercadoPago) {
            initMP();
        } else {
            const interval = setInterval(() => {
                if ((window as any).MercadoPago) { initMP(); clearInterval(interval); }
            }, 300);
            return () => clearInterval(interval);
        }
    }, []);

    useEffect(() => {
        const itemWithCoupon = items.find(item => item.couponCode);
        if (itemWithCoupon && !appliedCoupon && !couponCode) {
            setCouponCode(itemWithCoupon.couponCode!);
            handleApplyCoupon(itemWithCoupon.couponCode!);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items]);

    useEffect(() => {
        const checkOwnedProducts = async () => {
            if (!isAuthenticated || items.length === 0) return;
            try {
                const token = localStorage.getItem('accessToken');
                const response = await fetch('/api/payments/my-purchases', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    const owned = new Set<string>(
                        data.purchases.map((p: any) => {
                            const id = p.productId._id || p.productId;
                            return typeof id === 'string' ? id : String(id);
                        })
                    );
                    setOwnedProducts(owned);
                }
            } catch (error) {
                console.error('Erro ao verificar produtos:', error);
            }
        };
        checkOwnedProducts();
    }, [isAuthenticated, items]);

    // Adicione esse useEffect no início do componente, logo após os outros useEffects
    useEffect(() => {
        const previousTheme = document.documentElement.getAttribute('data-theme')
            || document.body.getAttribute('data-theme');

        document.documentElement.setAttribute('data-theme', 'light');
        document.body.setAttribute('data-theme', 'light');
        document.body.classList.remove('dark');
        document.documentElement.classList.remove('dark');

        return () => {
            // Restaura o tema original quando sair do checkout
            if (previousTheme) {
                document.documentElement.setAttribute('data-theme', previousTheme);
                document.body.setAttribute('data-theme', previousTheme);
            }
        };
    }, []);

    const formatPrice = (cents: number) =>
        (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    const subtotal = getTotal();
    const total = subtotal - discount;

    const getItemBadgeLabel = (type: string) => {
        if (type === 'deck' || type === 'custom_deck') return 'Deck';
        if (type === 'access_all') return 'Acesso Total';
        return 'Combo';
    };

    async function tokenizeCard(cardData: CardFormData): Promise<string> {
        const mp = (window as any).__mpInstance;
        if (!mp) throw new Error('SDK do Mercado Pago não disponível');

        // CORRIGIDO: suporte a ano com 2 ou 4 dígitos
        const [month, year] = cardData.expiry.split('/');
        const fullYear = year.length === 2 ? `20${year}` : year;

        const tokenResponse = await mp.createCardToken({
            cardNumber: cardData.cardNumber.replace(/\s/g, ''),
            cardholderName: cardData.cardHolder,
            cardExpirationMonth: month,
            cardExpirationYear: fullYear,
            securityCode: cardData.cvv,
            identificationType: 'CPF',
            identificationNumber: cardData.cpf,
        });

        if (tokenResponse.error) {
            throw new Error(
                tokenResponse.error?.message ||
                (typeof tokenResponse.error === 'string' ? tokenResponse.error : 'Erro ao tokenizar cartão')
            );
        }
        return tokenResponse.id;
    }

    async function callProcessTransparent(paymentData: any): Promise<any> {
        const token = localStorage.getItem('accessToken');
        const idempotencyKey = generateIdempotencyKey();

        const response = await fetch('/api/payments/process-transparent', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'Idempotency-Key': idempotencyKey,
            },
            body: JSON.stringify({
                paymentData,
                couponCode: appliedCoupon?.code,
            }),
        });

        const data = await response.json();

        if (!response.ok) {
            if (data.success === true) return data;
            throw new Error(data.message || 'Erro ao processar pagamento');
        }
        return data;
    }

    const handleSubmitCard = async (cardData: CardFormData) => {
        setProcessingPayment(true);
        setPaymentError('');
        try {
            const cardToken = await tokenizeCard(cardData);

            // CORRIGIDO: strip de espaços antes do slice para BIN correto
            const bin = cardData.cardNumber.replace(/\s/g, '').slice(0, 6);
            let paymentMethodId = 'visa';
            let issuerId: string | undefined;

            try {
                const mp = (window as any).__mpInstance;
                if (mp) {
                    const result = await mp.getInstallments({
                        amount: String(parseFloat((total / 100).toFixed(2))),
                        bin,
                        locale: 'pt-BR',
                    });
                    if (result?.[0]?.payment_method_id) {
                        paymentMethodId = result[0].payment_method_id;
                    }
                    // NOVO: capturar issuer_id para melhorar score antifraude
                    if (result?.[0]?.issuer?.id) {
                        issuerId = String(result[0].issuer.id);
                    }
                }
            } catch {
                console.warn('Não foi possível detectar bandeira, usando fallback');
            }

            const data = await callProcessTransparent({
                token: cardToken,
                payment_method_id: paymentMethodId,
                // NOVO: enviar issuer_id quando disponível
                ...(issuerId && { issuer_id: issuerId }),
                transaction_amount: parseFloat((total / 100).toFixed(2)),
                installments: cardData.installments,
                payer: {
                    email: user?.email,
                    identification: { type: 'CPF', number: cardData.cpf },
                },
                paymentType: cardData.paymentType === 'credit' ? 'credit_card' : 'debit_card',
                selectedPaymentMethod: cardData.paymentType === 'credit' ? 'credit_card' : 'debit_card',
            });

            // CORRIGIDO: verificar APENAS status 'approved', não 'in_process' nem 'success' genérico
            // in_process = pagamento ainda não confirmado, acesso NÃO deve ser liberado aqui
            // O acesso real é liberado pelo webhook quando o status chegar 'approved'
            if (data.payment?.status === 'approved') {
                setPurchasedType(items[0]?.product?.type || '');
                clearCart();
                setShowSuccessModal(true);
            } else if (data.payment?.status === 'in_process' || data.payment?.status === 'pending') {
                // Pagamento em análise — informar usuário mas NÃO liberar acesso aqui
                setPaymentError(
                    'Seu pagamento está em análise. Você receberá um e-mail quando for confirmado. ' +
                    'O acesso será liberado automaticamente após a aprovação.'
                );
            } else {
                throw new Error(data.message || 'Pagamento não aprovado');
            }
        } catch (err: any) {
            setPaymentError(err.message || 'Erro ao processar cartão');
        } finally {
            setProcessingPayment(false);
        }
    };

    const handleSubmitPix = async () => {
        setProcessingPayment(true);
        setPaymentError('');
        try {
            const data = await callProcessTransparent({
                formData: {
                    payment_method_id: 'pix',
                    transaction_amount: parseFloat((total / 100).toFixed(2)),
                    payer: { email: user?.email },
                },
                paymentType: 'bank_transfer',
                selectedPaymentMethod: 'bank_transfer',
            });

            if (data.payment?.pixData?.qrCode) {
                setPixData(data.payment.pixData);
                startPixPolling(data.payment.id);
            } else {
                throw new Error('QR Code não retornado');
            }
        } catch (err: any) {
            setPaymentError(err.message || 'Erro ao gerar Pix');
        } finally {
            setProcessingPayment(false);
        }
    };

    const handleSubmitBoleto = async (cpf: string, name: string, address: {
        zip_code: string; street_name: string; street_number: string;
        neighborhood: string; city: string; federal_unit: string;
    }) => {
        setProcessingPayment(true);
        setPaymentError('');
        try {
            const data = await callProcessTransparent({
                formData: {
                    payment_method_id: 'bolbradesco',
                    transaction_amount: parseFloat((total / 100).toFixed(2)),
                    payer: {
                        email: user?.email,
                        first_name: name.split(' ')[0],
                        last_name: name.split(' ').slice(1).join(' '),
                        identification: { type: 'CPF', number: cpf },
                        address,
                    },
                },
                paymentType: 'ticket',
                selectedPaymentMethod: 'ticket',
            });

            if (data.success) {
                if (data.payment?.boletoUrl) {
                    setBoletoUrl(data.payment.boletoUrl);
                } else {
                    setPurchasedType(items[0]?.product?.type || '');
                    clearCart();
                    setShowSuccessModal(true);
                }
            } else {
                throw new Error(data.message || 'Erro ao gerar boleto');
            }
        } catch (err: any) {
            setPaymentError(err.message || 'Erro ao gerar boleto');
        } finally {
            setProcessingPayment(false);
        }
    };

    const startPixPolling = (paymentId: string) => {
        setPixPolling(true);
        const currentType = items[0]?.product?.type || '';
        let attempts = 0;
        const maxAttempts = 60;

        const interval = setInterval(async () => {
            attempts++;
            try {
                const token = localStorage.getItem('accessToken');
                const response = await fetch(`/api/payments/pix-status/${paymentId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (response.ok) {
                    const data = await response.json();
                    if (data.status === 'approved') {
                        clearInterval(interval);
                        setPixPolling(false);
                        setPixData(null);
                        setPurchasedType(currentType);
                        clearCart();
                        setShowSuccessModal(true);
                    }
                }
            } catch (e) {
                console.error('Erro no polling do Pix:', e);
            }
            if (attempts >= maxAttempts) {
                clearInterval(interval);
                setPixPolling(false);
            }
        }, 5000);
    };

    const handleApplyCoupon = async (codeToApply?: string) => {
        const code = codeToApply || couponCode;
        if (!code.trim()) return;

        setCouponLoading(true);
        try {
            const token = localStorage.getItem('accessToken');
            const firstItem = items[0];
            if (!firstItem) return;

            const headers: Record<string, string> = { 'Content-Type': 'application/json' };
            if (token) headers.Authorization = `Bearer ${token}`;

            const response = await fetch('/api/payments/validate-coupon', {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    couponCode: code,
                    productId: firstItem.product._id,
                    planIndex: firstItem.product.plans.findIndex(p => p._id === firstItem.selectedPlan._id),
                }),
            });

            if (response.ok) {
                const data = await response.json();
                if (data.valid) {
                    const discountAmount = Math.round(subtotal * (data.coupon.discountPercent / 100));
                    setDiscount(discountAmount);
                    setAppliedCoupon({ code: data.coupon.code, discountPercent: data.coupon.discountPercent });
                    if (!codeToApply) setCouponCode(data.coupon.code);
                } else {
                    alert('Cupom inválido');
                }
            } else {
                alert('Cupom inválido');
            }
        } catch (error) {
            console.error('Erro ao aplicar cupom:', error);
        } finally {
            setCouponLoading(false);
        }
    };

    const handleRemoveCoupon = () => {
        setCouponCode('');
        setAppliedCoupon(null);
        setDiscount(0);
    };

    const handleNext = () => {
        if (currentStep === 'cart') {
            setCurrentStep(isAuthenticated ? 'payment' : 'auth');
        }
    };

    const handleAuthSuccess = () => {
        setTimeout(() => setCurrentStep('payment'), 500);
    };

    const renderHeader = () => {
        const steps = [
            { id: 'cart', label: 'Carrinho', icon: ShoppingCart },
            { id: 'auth', label: 'Identificação', icon: User },
            { id: 'payment', label: 'Pagamento', icon: CreditCard },
        ];
        const currentIndex = steps.findIndex(s => s.id === currentStep);

        return (
            <header className="ckeckout-header">
                <div className="ckeckout-header-left">
                    <img src="/graia-logo.png" alt="GRAIA" className="ckeckout-logo" />
                    <span className="ckeckout-brand">GRAIA</span>
                </div>
                <div className="ckeckout-stepper">
                    {steps.map((step, index) => {
                        const Icon = step.icon;
                        const isActive = step.id === currentStep;
                        const isCompleted = index < currentIndex;
                        return (
                            <React.Fragment key={step.id}>
                                <div className={`ckeckout-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}>
                                    <div className="ckeckout-step-circle">
                                        {isCompleted ? <CheckCircle size={20} strokeWidth={2.5} /> : <Icon size={20} strokeWidth={1.5} />}
                                    </div>
                                    <span className="ckeckout-step-label">{step.label}</span>
                                </div>
                                {index < steps.length - 1 && (
                                    <div className={`ckeckout-step-line ${isCompleted ? 'completed' : ''}`} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
                <div className="ckeckout-header-right">
                    <Shield size={20} />
                    <div className="ckeckout-security">
                        <span className="ckeckout-security-title">Ambiente 100% seguro</span>
                        <span className="ckeckout-security-subtitle">Pagamento via Mercado Pago</span>
                    </div>
                </div>
            </header>
        );
    };

    const renderCartStep = () => (
        <div className="ckeckout-content">
            <h2 className="ckeckout-title">Revise seu carrinho</h2>
            <p className="ckeckout-description">Confira os produtos selecionados antes de continuar</p>

            <div className="ckeckout-items">
                {items.map((item) => {
                    const isOwned = ownedProducts.has(item.product._id);
                    return (
                        <div key={item.product._id} className={`ckeckout-item ${isOwned ? 'owned' : ''}`}>
                            {isOwned && (
                                <div className="ckeckout-item-owned">
                                    <AlertCircle size={16} />
                                    <span>Você já possui este produto</span>
                                </div>
                            )}
                            <div className="ckeckout-item-info">
                                <div className="ckeckout-item-badge">
                                    {getItemBadgeLabel(item.product.type)}
                                </div>
                                <h3 className="ckeckout-item-title">{item.product.title}</h3>
                                <p className="ckeckout-item-description">{item.product.description}</p>
                                <div className="ckeckout-item-plan">
                                    <span>
                                        {item.selectedPlan.durationMonths === null
                                            ? 'Acesso Vitalício'
                                            : `${item.selectedPlan.durationMonths} meses`}
                                    </span>
                                </div>
                                {item.couponCode && (
                                    <div className="ckeckout-item-coupon">
                                        <Tag size={14} />
                                        <span>Cupom: {item.couponCode}</span>
                                    </div>
                                )}
                            </div>
                            <div className="ckeckout-item-actions">
                                <span className="ckeckout-item-price">{formatPrice(item.selectedPlan.priceCents)}</span>
                                <button className="ckeckout-btn-remove" onClick={() => removeItem(item.product._id)}>
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="ckeckout-coupon">
                <h3 className="ckeckout-coupon-title">Tem um cupom de desconto?</h3>
                {!appliedCoupon ? (
                    <div className="ckeckout-coupon-input">
                        <input
                            type="text"
                            placeholder="Digite o código do cupom"
                            value={couponCode}
                            onChange={e => setCouponCode(e.target.value.toUpperCase())}
                            disabled={couponLoading}
                        />
                        <button
                            onClick={() => handleApplyCoupon()}
                            disabled={!couponCode.trim() || couponLoading}
                            className="ckeckout-coupon-btn"
                        >
                            {couponLoading ? 'Verificando...' : 'Aplicar'}
                        </button>
                    </div>
                ) : (
                    <div className="ckeckout-coupon-applied">
                        <div className="ckeckout-coupon-info">
                            <Tag size={18} />
                            <span>Cupom <strong>{appliedCoupon.code}</strong> aplicado ({appliedCoupon.discountPercent}% de desconto)</span>
                        </div>
                        <button onClick={handleRemoveCoupon} className="ckeckout-coupon-remove"><X size={18} /></button>
                    </div>
                )}
            </div>

            <div className="ckeckout-summary">
                <div className="ckeckout-summary-row">
                    <span>Subtotal ({items.length} {items.length === 1 ? 'item' : 'itens'})</span>
                    <span>{formatPrice(subtotal)}</span>
                </div>
                {discount > 0 && (
                    <div className="ckeckout-summary-row discount">
                        <span>Desconto</span>
                        <span>- {formatPrice(discount)}</span>
                    </div>
                )}
                <div className="ckeckout-summary-row total">
                    <span>Total</span>
                    <span>{formatPrice(total)}</span>
                </div>
            </div>

            {ownedProducts.size > 0 && items.some(item => ownedProducts.has(item.product._id)) && (
                <div className="ckeckout-warning">
                    <AlertCircle size={20} />
                    <span>Você já possui alguns produtos do carrinho. Remova-os para continuar.</span>
                </div>
            )}

            <button
                className="ckeckout-btn-primary"
                onClick={handleNext}
                disabled={items.length === 0 || items.some(item => ownedProducts.has(item.product._id))}
            >
                {isAuthenticated ? 'Continuar para Pagamento' : 'Continuar para Identificação'}
            </button>
        </div>
    );

    const renderAuthStep = () => (
        <div className="ckeckout-content">
            <AuthStep onSuccess={handleAuthSuccess} />
        </div>
    );

    const renderPaymentStep = () => {
        if (pixData) {
            return (
                <div className="ckeckout-content">
                    <div className="ckeckout-pix-screen">
                        <div className="ckeckout-pix-header">
                            <div className="ckeckout-pix-badge">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M11.354 3.646a.9.9 0 0 1 1.272 0l2.496 2.497a3.6 3.6 0 0 0 5.087 0 .9.9 0 0 1 1.273 1.273 5.4 5.4 0 0 1-7.633 0L12 6.067l-1.85 1.849a5.4 5.4 0 0 1-7.632 0A.9.9 0 1 1 3.79 6.643a3.6 3.6 0 0 0 5.087 0l2.497-2.497ZM2.52 10.79a.9.9 0 0 1 1.27 0 3.6 3.6 0 0 0 5.087 0l1.85-1.85a.9.9 0 0 1 1.273 0l1.849 1.85a3.6 3.6 0 0 0 5.087 0 .9.9 0 1 1 1.272 1.271 5.4 5.4 0 0 1-7.633 0L12 10.212l-1.214 1.214v.001a5.4 5.4 0 0 1-7.633-.364.9.9 0 0 1 .368-1.273Zm.908 4.547a5.4 5.4 0 0 1 7.337.547l1.228 1.228 1.228-1.228a5.4 5.4 0 0 1 7.337-.547.9.9 0 1 1-1.097 1.43 3.6 3.6 0 0 0-4.968.364l-1.864 1.864a.9.9 0 0 1-1.272 0l-1.864-1.864a3.6 3.6 0 0 0-4.968-.364.9.9 0 1 1-1.097-1.43Z" />
                                </svg>
                                Pix
                            </div>
                            <h2 className="ckeckout-pix-title">Escaneie para pagar</h2>
                            <p className="ckeckout-pix-subtitle">Abra o app do seu banco e escaneie o QR Code abaixo</p>
                        </div>

                        {pixData.qrCodeBase64 && (
                            <div className="ckeckout-pix-qr-wrap">
                                <img
                                    src={`data:image/png;base64,${pixData.qrCodeBase64}`}
                                    alt="QR Code Pix"
                                    className="ckeckout-pix-qr-img"
                                />
                            </div>
                        )}

                        <div className="ckeckout-pix-copy-area">
                            <p className="ckeckout-pix-copy-label">Ou copie o código Pix</p>
                            <div className="ckeckout-pix-copy-row">
                                <input type="text" readOnly value={pixData.qrCode} className="ckeckout-pix-copy-input" />
                                <button
                                    className="ckeckout-pix-copy-btn"
                                    onClick={() => navigator.clipboard.writeText(pixData.qrCode)}
                                >
                                    Copiar
                                </button>
                            </div>
                        </div>

                        {pixPolling && (
                            <div className="ckeckout-pix-waiting">
                                <div className="ckeckout-spinner-sm" />
                                <span>Aguardando confirmação do pagamento...</span>
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        if (boletoUrl) {
            return (
                <div className="ckeckout-content">
                    <div className="ckeckout-boleto-success">
                        <div className="ckeckout-boleto-icon">
                            <CheckCircle size={48} color="#f59e0b" />
                        </div>
                        <h2>Boleto gerado!</h2>
                        <p>Pague até o vencimento. O acesso será liberado em até 3 dias úteis após a confirmação.</p>
                        <a
                            href={boletoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ckeckout-btn-primary"
                            style={{ display: 'block', textAlign: 'center', textDecoration: 'none', lineHeight: '54px' }}
                        >
                            Ver / Imprimir Boleto
                        </a>
                    </div>
                </div>
            );
        }

        return (
            <div className="ckeckout-content">
                <h2 className="ckeckout-title">Pagamento</h2>
                <p className="ckeckout-description">Escolha como deseja pagar</p>

                <div className="ckeckout-summary compact" style={{ marginBottom: 28 }}>
                    <div className="ckeckout-summary-row">
                        <span>Subtotal</span>
                        <span>{formatPrice(subtotal)}</span>
                    </div>
                    {discount > 0 && (
                        <div className="ckeckout-summary-row discount">
                            <span>Desconto</span>
                            <span>- {formatPrice(discount)}</span>
                        </div>
                    )}
                    <div className="ckeckout-summary-row total">
                        <span>Total a pagar</span>
                        <span>{formatPrice(total)}</span>
                    </div>
                </div>

                <GraiaPaymentForm
                    amount={total}
                    onSubmitCard={handleSubmitCard}
                    onSubmitPix={handleSubmitPix}
                    onSubmitBoleto={handleSubmitBoleto}
                    processing={processingPayment}
                    error={paymentError}
                />
            </div>
        );
    };

    const renderSuccessModal = () => {
        if (!showSuccessModal) return null;
        return (
            <div className="ckeckout-modal-overlay">
                <div className="ckeckout-modal">
                    <div className="ckeckout-modal-icon">
                        <CheckCircle size={64} strokeWidth={1.5} />
                    </div>
                    <h2>Pedido realizado com sucesso!</h2>
                    <p>
                        {purchasedType === 'access_all'
                            ? 'Seu pagamento foi confirmado. Você já tem acesso total à plataforma!'
                            : 'Seu pagamento foi confirmado. Você já tem acesso ao produto!'}
                    </p>
                    <div className="ckeckout-modal-actions">
                        <button
                            className="ckeckout-btn-primary"
                            onClick={() => { setShowSuccessModal(false); navigate('/dashboard'); }}
                        >
                            Ir para Dashboard
                        </button>
                        <button
                            className="ckeckout-btn-secondary"
                            onClick={() => { setShowSuccessModal(false); navigate('/marketplace'); }}
                        >
                            Continuar Comprando
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="ckeckout-page">
            {renderHeader()}
            <main className="ckeckout-main">
                {currentStep === 'cart' && renderCartStep()}
                {currentStep === 'auth' && renderAuthStep()}
                {currentStep === 'payment' && renderPaymentStep()}
            </main>
            {renderSuccessModal()}
        </div>
    );
};

export default CheckoutPageNew;